import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import UserAccountLayout, { type UserSettingsTab } from '@v2/features/users/UserAccountLayout';
import {
    AvatarUploadRow,
    PrefixInput,
    SettingsFormRow,
    settingsInputClass,
} from '@v2/features/users/UserSettingsFields';

type Profile = {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    department: string | null;
    job_title: string | null;
    locale: string | null;
    timezone: string | null;
    bio: string | null;
    avatar_url: string | null;
    role_names: string[];
};

type FormState = {
    name: string;
    email: string;
    phone: string;
    department: string;
    job_title: string;
    locale: string;
    timezone: string;
    bio: string;
    current_password: string;
    password: string;
    password_confirmation: string;
    avatar: File | null;
};

const empty = (): FormState => ({
    name: '',
    email: '',
    phone: '',
    department: '',
    job_title: '',
    locale: '',
    timezone: '',
    bio: '',
    current_password: '',
    password: '',
    password_confirmation: '',
    avatar: null,
});

const LOCALES = [
    { value: '', label: 'Padrão do sistema' },
    { value: 'pt_BR', label: 'Português (Brasil)' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'zh_CN', label: 'Chinês' },
];

const TABS = [
    { key: 'profile' as const, label: 'Perfil' },
    { key: 'password' as const, label: 'Senha' },
];

export default function ProfilePage() {
    const qc = useQueryClient();
    const [activeTab, setActiveTab] = useState<UserSettingsTab>('profile');
    const [form, setForm] = useState<FormState>(empty);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [preview, setPreview] = useState<string | null>(null);

    const profileQuery = useQuery({
        queryKey: ['profile'],
        queryFn: async (): Promise<Profile> => (await api.get('/profile')).data.data,
    });

    useEffect(() => {
        if (profileQuery.data) {
            const u = profileQuery.data;
            setForm({
                ...empty(),
                name: u.name ?? '',
                email: u.email ?? '',
                phone: u.phone ?? '',
                department: u.department ?? '',
                job_title: u.job_title ?? '',
                locale: u.locale ?? '',
                timezone: u.timezone ?? '',
                bio: u.bio ?? '',
            });
            setPreview(u.avatar_url ?? null);
        }
    }, [profileQuery.data]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const onAvatarChange = (file: File | null) => {
        set('avatar', file);
        setPreview(file ? URL.createObjectURL(file) : profileQuery.data?.avatar_url ?? null);
    };

    const mutation = useMutation({
        mutationFn: async () => {
            const fd = new FormData();
            fd.append('_method', 'POST');
            const append = (k: string, v: unknown) => {
                if (v === null || v === undefined || v === '') return;
                fd.append(k, String(v));
            };
            append('name', form.name);
            append('email', form.email);
            append('phone', form.phone);
            append('department', form.department);
            append('job_title', form.job_title);
            append('locale', form.locale);
            append('timezone', form.timezone);
            append('bio', form.bio);
            if (form.password) {
                append('current_password', form.current_password);
                append('password', form.password);
                append('password_confirmation', form.password_confirmation);
            }
            if (form.avatar) fd.append('avatar', form.avatar);
            return (await api.post('/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
        },
        onSuccess: () => {
            setError(null);
            setFieldErrors({});
            setSuccess('Perfil atualizado com sucesso.');
            set('current_password', '');
            set('password', '');
            set('password_confirmation', '');
            set('avatar', null);
            qc.invalidateQueries({ queryKey: ['profile'] });
            qc.invalidateQueries({ queryKey: ['me'] });
        },
        onError: (err: { response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } }) => {
            setSuccess(null);
            if (err?.response?.status === 422) {
                setFieldErrors(err.response.data?.errors ?? {});
                setError(err.response.data?.message ?? 'Verifique os campos destacados.');
            } else {
                setError(err?.response?.data?.message ?? 'Erro ao salvar.');
            }
        },
    });

    if (profileQuery.isLoading) return <LoadingState label="Carregando perfil…" />;

    const u = profileQuery.data;
    const fe = (k: string) => fieldErrors[k]?.[0];
    const subtitle = [u?.job_title, u?.department].filter(Boolean).join(' · ') || 'Atualize sua foto e dados pessoais.';

    return (
        <UserAccountLayout
            pageTitle="Configurações da conta"
            breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Meu perfil' }]}
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            user={{ name: form.name || u?.name || '', avatar_url: preview, subtitle }}
            headerAction={
                <Button
                    type="button"
                    className="mb-1 bg-gray-900 text-white hover:bg-gray-800"
                    onClick={() => mutation.mutate()}
                    disabled={mutation.isPending}
                >
                    {mutation.isPending ? 'Salvando…' : 'Salvar alterações'}
                </Button>
            }
        >
            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}
            {success && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {success}
                </div>
            )}

            {activeTab === 'profile' && (
                <>
                    <AvatarUploadRow preview={preview} onChange={onAvatarChange} />

                    <SettingsFormRow label="Nome completo" required error={fe('name')}>
                        <input className={settingsInputClass} value={form.name} onChange={(e) => set('name', e.target.value)} />
                    </SettingsFormRow>

                    <SettingsFormRow label="E-mail" hint="Endereço usado para login." required error={fe('email')}>
                        <input
                            type="email"
                            className={settingsInputClass}
                            value={form.email}
                            onChange={(e) => set('email', e.target.value)}
                        />
                    </SettingsFormRow>

                    <SettingsFormRow label="Telefone" error={fe('phone')}>
                        <PrefixInput prefix="+55" value={form.phone} onChange={(v) => set('phone', v)} placeholder="11999998888" />
                    </SettingsFormRow>

                    <SettingsFormRow label="Cargo" error={fe('job_title')}>
                        <input className={settingsInputClass} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} />
                    </SettingsFormRow>

                    <SettingsFormRow label="Departamento" error={fe('department')}>
                        <input className={settingsInputClass} value={form.department} onChange={(e) => set('department', e.target.value)} />
                    </SettingsFormRow>

                    <SettingsFormRow label="Idioma" error={fe('locale')}>
                        <select className={settingsInputClass} value={form.locale} onChange={(e) => set('locale', e.target.value)}>
                            {LOCALES.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </SettingsFormRow>

                    <SettingsFormRow label="Fuso horário" error={fe('timezone')}>
                        <input
                            className={settingsInputClass}
                            placeholder="America/Sao_Paulo"
                            value={form.timezone}
                            onChange={(e) => set('timezone', e.target.value)}
                        />
                    </SettingsFormRow>

                    <SettingsFormRow label="Sobre você" error={fe('bio')}>
                        <textarea
                            className={`${settingsInputClass} min-h-28`}
                            value={form.bio}
                            onChange={(e) => set('bio', e.target.value)}
                        />
                    </SettingsFormRow>

                    {u?.role_names?.length ? (
                        <SettingsFormRow label="Funções" hint="Atribuídas por um administrador.">
                            <div className="flex flex-wrap gap-2">
                                {u.role_names.map((r) => (
                                    <span
                                        key={r}
                                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
                                    >
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </SettingsFormRow>
                    ) : null}
                </>
            )}

            {activeTab === 'password' && (
                <>
                    <SettingsFormRow label="Senha atual" error={fe('current_password')}>
                        <input
                            type="password"
                            className={settingsInputClass}
                            value={form.current_password}
                            onChange={(e) => set('current_password', e.target.value)}
                        />
                    </SettingsFormRow>
                    <SettingsFormRow label="Nova senha" hint="Mínimo 8 caracteres." error={fe('password')}>
                        <input
                            type="password"
                            className={settingsInputClass}
                            value={form.password}
                            onChange={(e) => set('password', e.target.value)}
                        />
                    </SettingsFormRow>
                    <SettingsFormRow label="Confirmar nova senha" error={fe('password_confirmation')}>
                        <input
                            type="password"
                            className={settingsInputClass}
                            value={form.password_confirmation}
                            onChange={(e) => set('password_confirmation', e.target.value)}
                        />
                    </SettingsFormRow>
                </>
            )}
        </UserAccountLayout>
    );
}
